import type { ChallengeResponse, ChallengeShareTarget, ShareAttemptChallenge } from '@/contracts';

type ChallengeShareLinkFields = Pick<
  ChallengeResponse,
  | 'challenge_url'
  | 'default_utm_source'
  | 'default_utm_medium'
  | 'default_utm_campaign'
  | 'default_utm_content'
  | 'default_utm_id'
>;

type ChallengeShareLinkOption = {
  target: ChallengeShareTarget;
  label: string;
};

const DIRECT_SHARE_TARGET: ChallengeShareTarget = 'direct';
const PLATFORM_SHARE_MEDIUM = 'organic_social';

export const CHALLENGE_SHARE_LINK_OPTIONS: readonly ChallengeShareLinkOption[] = [
  {
    target: DIRECT_SHARE_TARGET,
    label: 'Direct',
  },
  {
    target: 'instagram',
    label: 'Instagram',
  },
  {
    target: 'facebook',
    label: 'Facebook',
  },
  {
    target: 'tiktok',
    label: 'TikTok',
  },
];

const resolveChallengeShareLinkOption = (
  target: ChallengeShareTarget
): ChallengeShareLinkOption => {
  const resolvedOption = CHALLENGE_SHARE_LINK_OPTIONS.find(
    (option) => option.target === target
  );

  if (resolvedOption === undefined) {
    throw new Error(`Unsupported challenge share target: ${target}`);
  }

  return resolvedOption;
};

const buildTaggedContent = (
  challenge: ChallengeShareLinkFields,
  target: ChallengeShareTarget
): string => {
  if (target === DIRECT_SHARE_TARGET) {
    return challenge.default_utm_content;
  }

  const taggedContent = `${challenge.default_utm_content}_${target}`;
  return taggedContent;
};

const buildTaggedSource = (
  challenge: ChallengeShareLinkFields,
  target: ChallengeShareTarget
): string => {
  if (target === DIRECT_SHARE_TARGET) {
    return challenge.default_utm_source;
  }

  const taggedSource = target;
  return taggedSource;
};

const buildTaggedMedium = (
  challenge: ChallengeShareLinkFields,
  target: ChallengeShareTarget
): string => {
  if (target === DIRECT_SHARE_TARGET) {
    return challenge.default_utm_medium;
  }

  return PLATFORM_SHARE_MEDIUM;
};

export const buildChallengeShareLink = (
  challenge: ChallengeShareLinkFields | ShareAttemptChallenge,
  target: ChallengeShareTarget
): string => {
  const shareUrl = new URL(challenge.challenge_url);
  const taggedContent = buildTaggedContent(challenge, target);
  const taggedSource = buildTaggedSource(challenge, target);
  const taggedMedium = buildTaggedMedium(challenge, target);

  shareUrl.searchParams.set('utm_source', taggedSource);
  shareUrl.searchParams.set('utm_medium', taggedMedium);
  shareUrl.searchParams.set('utm_campaign', challenge.default_utm_campaign);
  shareUrl.searchParams.set('utm_content', taggedContent);
  shareUrl.searchParams.set('utm_id', challenge.default_utm_id);

  const taggedShareUrl = shareUrl.toString();
  return taggedShareUrl;
};

export const formatChallengeShareTargetLabel = (
  target: ChallengeShareTarget
): string => {
  const option = resolveChallengeShareLinkOption(target);
  const label = option.label;
  return label;
};
